import "../styles/send-alert.scss";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import actions from "../actions/actions";
import api from "../api/api";
import { appState } from "../redux/redux-config";
import { GuiCheckbox } from "../components/global-ui/gui-checkbox";
import { dispatch } from "../redux/redux-config";
import {
  AlertType,
  TemplateType,
  SMS_CHAR_LIMIT,
  SmsCreditTiers,
  AlertPriorityLevel,
  MAX_TTS_DURATION_SECONDS,
  TTS_CHAR_LIMIT,
  TTS_CHAR_WARNING_LIMIT,
  TTS_TRIM_TARGET_SECONDS,
} from "../utilities/constants";
import {
  formatDateTimeIso,
  formatGridDate,
  useUpdateState,
  getAudioDurationSeconds,
  estimateTtsDurationSecondsCalibrated,
  formatDurationShort,
  trimTtsTextToDuration,
} from "../utilities/utilities";
import GuiHtmlEditor from "../components/global-ui/gui-html-editor";
import * as DOMPurify from "dompurify";
import { GuiTextbox } from "../components/global-ui/gui-textbox";
import { GuiTextArea } from "../components/global-ui/gui-textarea";
import { VoiceRecorder } from "../components/voice-recorder";
import ValidationContext, {
  useValidation,
} from "../components/global-ui/validation";
import { Modal } from "../components/modal";
import Error from "../components/global-ui/error";
import { TemplatePickerModal } from "../components/template-picker-modal";
import { TabItem } from "../components/tab-item";
import { GuiButton } from "../components/global-ui/gui-button";
import { ConfirmCloseModal } from "../components/confirm-close-modal";
import { ConfirmDeleteModal } from "../components/confirm-delete-modal";
import { GroupPicker } from "../components/group-picker";
import LoadingSpinner, { Spinner } from "../components/loading-spinner";
import ScheduleBaseAlertModal from "../components/schedule-base-alert-modal";
import AudioPlayer from "../components/audio-player";
import { showToast } from "../components/toast";
import {
  useNavState,
  pushNavState,
  popNavState,
} from "../utilities/navigation";
import navigate from "../utilities/navigation";
import ExpandPanel from "../components/expand-panel";
import { DISCARD_ROUTES } from "../utilities/constants";
import { GuiSelect } from "../components/global-ui/gui-select";
import user from "../actions/user-actions";
import TranslationControls from "../components/translation-controls";
import {
  UNIVERSAL_SPAM_WARNING,
  detectSpamPatterns,
} from "../utilities/spam-detection-constants";
import { trackPendo } from "../utilities/pendo-events";

export default function SendAlert(props) {
  let loginUser = useSelector((store) => store.user);
  // Unified fixed SMS character limit; legacy product-specific logic removed.
  const [productId, setProductId] = useState(null); // store product id for quick launch naming
  // 2-way text dropdown state
  const [requestResponse, setRequestResponse] = useState("0");
  const [requestEmailResponse, setRequestEmailResponse] = useState("");
  let alert = useSelector((store) => store.alert.current);
  let { step, saving } = useSelector((store) => store.alert);
  let isMobile = useSelector((store) => store.page.isMobile);
  let { pickGroups, confirmClose, pickTemplate, confirmSave, confirmDiscard } =
    useNavState((loc) => loc);
  const [from, setFrom] = useState(DISCARD_ROUTES.dashboard);

  // Email and Sender Names from redux store

  // Build dropdown options from emailSenderNames (Email as label, Id as value)

  let initialState = useMemo(
    () => ({
      voiceTab:
        alert?.voiceFileIdentifier && alert?.t2sUrl ? "texttospeech" : "record",
      lastTTSMessage: alert?.voiceFileIdentifier ? alert?.t2sMessage : null,
    }),
    [],
  );
  let [state, updateState] = useUpdateState(initialState);
  const [isEmergencyQuickLaunchApplied, setIsEmergencyQuickLaunchApplied] =
    useState(false);
  const [usedTemplate, setUsedTemplate] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [emailDataSender, setEmailDataSender] = useState([]);
  const [groupId, setGroupId] = useState(0);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [spamResults, setSpamResults] = useState({
    text: { warnings: [], score: 0, buttonShow: true },
    voice: { warnings: [], score: 0, buttonShow: true },
    email: { warnings: [], score: 0, buttonShow: true },
  });

  const detectSpamPatternsCallback = useCallback((text, type = "text") => {
    const normalizedType =
      type === "voice" ? "voice" : type === "email" ? "email" : "text";
    const detectionResult = detectSpamPatterns(text);
    const enrichedResult = {
      warnings: detectionResult.buttonShow ? [] : [UNIVERSAL_SPAM_WARNING],
      score: detectionResult.score,
      buttonShow: detectionResult.buttonShow,
    };

    setSpamResults((prev) => ({
      ...prev,
      [normalizedType]: enrichedResult,
    }));

    return enrichedResult;
  }, []);
  const hasSpamContent = useMemo(
    () =>
      Object.values(spamResults).some(
        (result) => result && result.buttonShow === false,
      ),
    [spamResults],
  );

  const renderSpamWarnings = (type) => {
    const result = spamResults[type];
    if (!result || result.buttonShow !== false) return null;

    return (
      <div className="spam-warning-container">
        <div className="spam-warning-message">{UNIVERSAL_SPAM_WARNING}</div>
      </div>
    );
  };
  // Escalation (redux-based)
  const escalation = useSelector((s) => s.escalation);
  const escalationLevelOptions = escalation.levels?.length
    ? [{ value: "", label: "Select" }, ...escalation.levels]
    : [{ value: "", label: "Select" }];
  let emailHtmlEditor = useRef();
  const scheduleTriggerRef = useRef(null);
  const fileInputRef = useRef(null);
  const fileAttachTargetRef = useRef(null);
  const [isFileUploading, setIsFileUploading] = useState(false);

  const MAX_ATTACH_FILE_SIZE_MB = 5;

  let validation = useValidation(validate, [alert, state]);
  let howTabValid = !validation.getError("tab0");
  let whoTabValid = !validation.getError("tab1");
  let messageTabValid = !validation.getError("tab2");
  const [isTempGroupExists, setIsTempGroupExists] = useState(false);

  const fetchUserProduct = async () => {
    try {
      const result = await user.getUserProduct();
      if (result && result?.success) {
        setProductId(result?.productId);
      }
    } catch (error) {
      console.error("Error fetching user product:", error);
    }
  };

  useEffect(() => {
    fetchUserProduct();
  }, []);

  useEffect(() => {
    actions.escalation.setSelection("specify", "");
  }, []);

  // Load Reply-To email + Sender Name options on mount
  const loadEmailAndSenderNames = async () => {
    const result = await actions.messages.loadEmailAndSenderNames();
    setEmailDataSender(result);
  };
  useEffect(() => {
    loadEmailAndSenderNames();
  }, []);

  // Keep Reply-To selection in sync when editing an existing alert.
  useEffect(() => {
    setRequestEmailResponse(alert?.replyTo ? alert.replyTo.toString() : "");
  }, [alert?.id, alert?.replyTo]);

  // Auto-set default replyTo + emailSenderName from first row once data loads
  useEffect(() => {
    if (emailDataSender.length > 0 && !alert?.replyTo) {
      const first = emailDataSender[0];
      setRequestEmailResponse(first.email?.toString() || "");
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        emailSenderName: first.senderName || "",
      });
    }
  }, [emailDataSender]);

  useEffect(() => {
    if (alert?.isEmergency) {
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        includeText: true,
        includeEmail: true,
        includeVoice: true,
        name: alert?.name || "Emergency",
        smsMessage: alert?.smsMessage || "Emergency:",
        emailMessage: alert?.emailMessage || "Emergency:",
        t2sMessage: alert?.t2sMessage || "Emergency:",
      });
    }
  }, [alert?.isEmergency]);

  // Load escalation levels only when user expands the first panel (requested behavior)
  function onHowPanelHeaderClick() {
    if (step === 0) {
      const types = [];
      let messageTypes = "";
      if (alert?.includeText) {
        types.push("text");
      }
      if (alert?.includeEmail) {
        types.push("email");
      }
      if (alert?.includeVoice) {
        types.push("voice");
      }
      //of all type is included then in messagetypes = "allmethods"
      messageTypes = types.join(",");
      if (types.length === 3) {
        messageTypes = "allmethods";
      }
      if (messageTypes.length > 0) {
        actions.escalation.loadLevels(messageTypes);
      }
    }
    changeStep(1);
  }

  useEffect(() => {
    if (alert?.alertType === AlertType.Text) {
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        includeText: true,
        smsMessage: alert?.message,
      });
    }
    if (alert?.alertType === AlertType.Email) {
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        includeEmail: true,
        emailMessage: alert?.message,
      });
    }
    if (alert?.alertType === AlertType.Voice) {
      dispatch("UPDATE_NEW_ALERT", {
        ...alert,
        includeVoice: true,
        t2sMessage: alert?.message,
        t2sUrl: alert?.voiceFileUrl,
      });
    }
  }, [alert?.alertType]);

  useEffect(() => {
    if (alert?.includeText || alert?.includeVoice) {
      detectSpamPatternsCallback(alert?.smsMessage || "", "text");
    } else {
      detectSpamPatternsCallback("", "text");
    }
  }, [
    alert?.includeText,
    alert?.includeVoice,
    alert?.smsMessage,
    detectSpamPatternsCallback,
  ]);

  useEffect(() => {
    if (alert?.includeVoice && state.voiceTab === "texttospeech") {
      detectSpamPatternsCallback(alert?.t2sMessage || "", "voice");
    } else {
      detectSpamPatternsCallback("", "voice");
    }
  }, [
    alert?.includeVoice,
    alert?.t2sMessage,
    state.voiceTab,
    detectSpamPatternsCallback,
  ]);

  // Email spam detection (strip HTML to analyze text content)
  useEffect(() => {
    if (alert?.includeEmail) {
      const raw =
        typeof alert?.emailMessage === "string" ? alert.emailMessage : "";
      const emailText = raw
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/\s+/g, " ")
        .trim();
      detectSpamPatternsCallback(emailText || "", "email");
    } else {
      detectSpamPatternsCallback("", "email");
    }
  }, [alert?.includeEmail, alert?.emailMessage, detectSpamPatternsCallback]);

  useEffect(() => {
    if (props.location?.state?.from) {
      setFrom(props.location.state.from);
    }
  }, [props.location?.state?.from]);

  useEffect(() => {
    if (alert?.groups?.length > 0) {
      const group = alert?.groups.find((g) => g.is_temp);
      if (group) {
        setIsTempGroupExists(true);
        if (group.id > 0) {
          setGroupId(group?.id);
          getGroupDetails(group?.id);
        } else {
          // Background group creation still in progress
          setLoadingMembers(true);
        }
      } else {
        setIsTempGroupExists(false);
        setGroupMembers([]);
        setLoadingMembers(false);
      }
    }
  }, [alert.groups]);

  const getGroupDetails = async (id) => {
    setLoadingMembers(true);
    try {
      const details = await actions.groups.getAllGroupMembers(id);

      if (details?.members) {
        setGroupMembers(details.members);
      } else {
        showToast("warning", "No group members found.");
        setGroupMembers([]);
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
      showToast("error", "Failed to fetch group details.");
    } finally {
      setLoadingMembers(false);
    }
  };

  alert.isEmergency =
    useSelector((store) => store.router.location.pathname) ===
    "/send/emergency";

  function toggleType(type) {
    const nextAlert = {
      ...alert,
      [type]: !alert[type],
    };

    // If switching to multi-type and Text is selected, sync email fields
    const types = [
      nextAlert.includeText,
      nextAlert.includeEmail,
      nextAlert.includeVoice,
    ];
    if (
      types.filter(Boolean).length > 1 &&
      nextAlert.includeText &&
      nextAlert.includeEmail
    ) {
      // Only update if emailMessage is empty or different
      if (
        !nextAlert.emailMessage ||
        nextAlert.emailMessage !== nextAlert.smsMessage
      ) {
        nextAlert.emailMessage = nextAlert.smsMessage || "";
      }
      // Sync subject if needed
      nextAlert.emailSubject = nextAlert.name;
    }

    dispatch("UPDATE_NEW_ALERT", nextAlert);
    updateState({
      previewed: false,
      ttsTrim: null,
      ttsDurationSeconds: null,
      ttsCharsPerSecond: null,
      ttsBannerDismissed: false,
    });
  }

  async function handleRemoveUser(userId) {
    try {
      const result = await actions.groups.removePeopleFromGroup(
        groupId,
        userId,
      );

      if (result?.success) {
        showToast("success", "User removed successfully");
        await getGroupDetails(groupId);
      } else {
        showToast("error", result?.message);
      }
    } catch (error) {
      console.error("Error removing user:", error);
      showToast(
        "error",
        "An unexpected error occurred while removing the user.",
      );
    }
  }

  function update(name, value) {
    dispatch("UPDATE_NEW_ALERT", { ...alert, [name]: value });
    updateState({ previewed: false });
  }

  async function changeStep(step) {
    if (step > 0 && !howTabValid) return;
    if (step > 1 && !whoTabValid) return;
    if (step > 2 && !messageTabValid) return;

    // If moving to step 3 (Preview and Send), auto-convert TTS if needed
    if (
      step === 3 &&
      isMultiType(alert) &&
      alert?.smsMessage &&
      (alert?.t2sMessage !== alert?.smsMessage || !alert?.t2sUrl)
    ) {
      updateState({ convertingTTS: true, ttsDurationSeconds: null });
      const result = await actions.alerts.textToSpeech(alert?.smsMessage);
      const durationSeconds = result?.success
        ? await getAudioDurationSeconds(result?.fileUrl)
        : null;
      updateState({
        convertingTTS: false,
        lastTTSMessage: alert?.smsMessage,
        ttsDurationSeconds: durationSeconds,
      });
      if (result.success) {
        dispatch("UPDATE_NEW_ALERT", {
          ...alert,
          t2sMessage: alert?.smsMessage,
          t2sUrl: result?.fileUrl,
          voiceFileIdentifier: result?.fileIdentifier,
        });
      }
      if (durationSeconds > MAX_TTS_DURATION_SECONDS) return;
    }

    actions.alerts.changeStep(step);
    if (step === 3) updateState({ previewed: true });
  }

  function discardAlert() {
    navigate(from);
  }

  function showPopup(popup) {
    pushNavState({ [popup]: true });
  }

  function hidePopup() {
    popNavState();
  }

  function groupsChanged(selected) {
    dispatch("UPDATE_NEW_ALERT", { ...alert, groups: selected });
    updateState({ previewed: false });
    hidePopup();
  }

  function removeGroup(group) {
    dispatch("UPDATE_NEW_ALERT", {
      ...alert,
      groups: alert.groups.filter((g) => g !== group),
    });
    updateState({ previewed: false });
  }

  async function convertTTS(textOverride) {
    // textOverride is only ever a string when called from a trim action; button
    // onClick handlers pass a DOM event, so guard against treating that as text.
    const overrideText = typeof textOverride === "string" ? textOverride : null;
    const textToConvert = overrideText || alert?.t2sMessage;
    if (!state?.convertingTTS && textToConvert) {
      updateState({ convertingTTS: true, ttsDurationSeconds: null });
      var result = await actions.alerts.textToSpeech(textToConvert);
      const durationSeconds = result?.success
        ? await getAudioDurationSeconds(result?.fileUrl)
        : null;
      // Calibrate the estimator to the rate actually observed for this conversion,
      // so the live counter stops drifting from a generic per-language guess.
      const charsPerSecond =
        durationSeconds > 0 ? textToConvert.length / durationSeconds : null;
      updateState({
        convertingTTS: false,
        lastTTSMessage: alert?.t2sMessage,
        ttsDurationSeconds: durationSeconds,
        ttsCharsPerSecond: charsPerSecond,
        ttsBannerDismissed: false,
      });

      if (result?.success) {
        dispatch("UPDATE_NEW_ALERT", {
          ...alert,
          t2sUrl: result?.fileUrl,
          voiceFileIdentifier: result?.fileIdentifier, // Add this line
        });
      }
    }
  }

  // Never truncates or rate-adjusts audio — only re-converts a shorter, sentence-aligned
  // portion of the source text. The dropped remainder is kept (not deleted) for display.
  function applyTtsTrim(targetSeconds) {
    const { keptText, overflowText, trimmed } = trimTtsTextToDuration(
      alert?.t2sMessage,
      state?.ttsDurationSeconds,
      targetSeconds,
    );
    if (!trimmed || !keptText) return;
    updateState({ ttsTrim: { keptText, overflowText } });
    convertTTS(keptText);
  }

  function handleShortenToFit() {
    // Small safety margin so the re-converted audio reliably lands under the hard limit.
    applyTtsTrim(MAX_TTS_DURATION_SECONDS - 5);
  }

  function handleTrimTo105() {
    applyTtsTrim(TTS_TRIM_TARGET_SECONDS);
  }

  function dismissTtsDurationBanner() {
    updateState({ ttsBannerDismissed: true });
  }

  function handleTtsMessageChange(name, value) {
    dispatch("UPDATE_NEW_ALERT", {
      ...alert,
      [name]: (value || "").slice(0, TTS_CHAR_LIMIT),
    });
    updateState({ previewed: false, ttsTrim: null, ttsBannerDismissed: false });
  }

  function applyEmergencyQuickLaunch() {
    trackPendo("emergency_alert_launched", {});
    setIsEmergencyQuickLaunchApplied(true);
    // Load escalation levels for all message types
    actions.escalation.loadLevels("allmethods");
    // 1. Set mode to 'specify' and select dropdown value (value = 1)
    actions.escalation.setSelection("quick", 1);

    const isAnnouncement = productId === 58; // legacy product 58 => Announcement
    const subject = isAnnouncement
      ? "Emergency Announcement"
      : "Emergency Alert";
    const bodyPrefix = isAnnouncement
      ? "Emergency Announcement: "
      : "Emergency Alert: ";
    const updated = {
      ...alert,
      includeText: true,
      includeEmail: true,
      includeVoice: true,
      // Mark as emergency for priority mapping (existing save uses isEmergency flag)
      isEmergency: true,
      allGroups: true, // Ensure all groups are checked
      name: subject,
      emailSubject: subject,
      smsMessage: bodyPrefix,
      emailMessage: bodyPrefix,
      t2sMessage: bodyPrefix,
      t2sUrl: "",
      voiceFileIdentifier: "",
      voiceFileUrl: "",
      voicePreviewUrl: "",
    };

    dispatch("UPDATE_NEW_ALERT", updated);
    updateState({ previewed: false });
  }

  function updateVoiceRecording(name, value) {
    dispatch("UPDATE_NEW_ALERT", {
      ...alert,
      [name]: value,
      voiceFileIdentifier: null,
      voiceFileUrl: null,
      voicePreviewUrl: null,
    });
    updateState({ previewed: false });
  }

  function onShowTemplatePicker(type) {
    updateState({ templatePickerType: type });
    showPopup("pickTemplate");
  }

  function useTemplate(template) {
    if (!template) return;

    let update = { ...alert };
    update.name = template.name;
    let newVoiceTab = state?.voiceTab;

    if (state.templatePickerType === AlertType.Text) {
      update.smsMessage = template.message;
    } else if (state.templatePickerType === AlertType.Email) {
      update.emailMessage = template.message;
      emailHtmlEditor.current.setHtml(template.message);
    } else if (state.templatePickerType === "voice") {
      if (template.templateType === TemplateType.VOICE) {
        // Pre-recorded audio file template. `voicePreviewUrl` is a locally
        // playable URL (blob URL or embedded data: URL) used purely for the
        // <audio> preview. The actual value submitted to the backend is
        // either a real blob URL (voiceFileUrl) or a CDYNE soundFileId
        // (voiceFileIdentifier), whichever the template originally used.
        update.voicePreviewUrl = template.voiceAudioUrl || "";
        update.voiceFileUrl = template.voiceFileUrl || "";
        update.voiceFileIdentifier = template.voiceFileIdentifier || null;
        update.voiceRecording = null;
        update.t2sUrl = "";
        newVoiceTab = "record";
      } else {
        // Text-to-speech template — insert as text and switch to the Text to Voice tab.
        update.t2sMessage = template.message;
        update.t2sUrl = "";
        update.voiceFileIdentifier = "";
        newVoiceTab = "texttospeech";
      }
    }

    setUsedTemplate(true);
    dispatch("UPDATE_NEW_ALERT", update);
    updateState({
      templatePickerType: null,
      previewed: false,
      voiceTab: newVoiceTab,
    });
    hidePopup();
  }

  function onAttachmentAdded(name, info) {
    dispatch("UPDATE_NEW_ALERT", {
      ...alert,
      attachments: [...alert?.attachments, info],
    });
    updateState({ previewed: false });
  }

  function removeAttachment(attachment) {
    dispatch("REMOVE_ATTACHMENT", attachment);
    updateState({ previewed: false });
  }

  function onFileAttachClicked(target) {
    fileAttachTargetRef.current = target;
    fileInputRef.current.value = null;
    fileInputRef.current.click();
  }

  async function onFileAttachSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_ATTACH_FILE_SIZE_MB * 1000 * 1000) {
      showToast(
        "error",
        "File size must not exceed 5 MB. Please try another file.",
      );
      return;
    }

    setIsFileUploading(true);
    const token = appState().user.token;
    const target = fileAttachTargetRef.current;

    api.file.uploadAttachment(
      token,
      file,
      async (progress) => {
        if (!progress.complete) return;

        if (!progress.result.success) {
          showToast("error", "Error uploading the file.");
          setIsFileUploading(false);
          return;
        }

        const shortenResult = await api.file.shortenUrl(
          token,
          progress.result.url,
        );
        setIsFileUploading(false);

        if (!shortenResult?.success) {
          showToast("error", "Error uploading the file.");
          return;
        }

        const shortUrl = shortenResult.shortUrl;
        const fileName = progress.name;
        const updatedAttachments = [
          ...(alert?.attachments ?? []),
          { name: fileName, url: shortUrl, mimeType: file.type },
        ];

        if (target === "sms") {
          const current = alert?.smsMessage || "";
          const separator = current.length > 0 ? " " : "";
          const newSmsMessage = current + separator + shortUrl;
          if (newSmsMessage.length > SMS_CHAR_LIMIT) {
            showToast(
              "error",
              "Not enough available characters to insert this link.",
            );
            return;
          }
          dispatch("UPDATE_NEW_ALERT", {
            ...alert,
            smsMessage: newSmsMessage,
            attachments: updatedAttachments,
          });
        } else if (target === "email") {
          const newEmailHtml = emailHtmlEditor.current?.insertHtml(
            `<a href="${shortUrl}">${shortUrl}</a>`,
          );
          dispatch("UPDATE_NEW_ALERT", {
            ...alert,
            emailMessage: newEmailHtml ?? alert?.emailMessage,
            attachments: updatedAttachments,
          });
        } else {
          dispatch("UPDATE_NEW_ALERT", {
            ...alert,
            attachments: updatedAttachments,
          });
        }

        showToast("success", "Link to attachment successfully added!");
        updateState({ previewed: false });
      },
      MAX_ATTACH_FILE_SIZE_MB,
    );
  }

  function scheduleClick(event) {
    if (!validation.allValid()) return validation.setShow(true);

    scheduleTriggerRef.current = event?.currentTarget || null;

    showPopup("confirmSave");
  }

  function closeScheduleModal() {
    hidePopup();

    if (scheduleTriggerRef.current?.focus) {
      setTimeout(() => scheduleTriggerRef.current?.focus(), 0);
    }
  }

  function saveSchedule(dates) {
    closeScheduleModal();
    save(false, dates);
  }

  async function save(isDraft, scheduledDates) {
    isDraft = isDraft === true;

    if (!isDraft && !validation.allValid()) return validation.setShow(true);

    const normalizedScheduledDates = Array.isArray(scheduledDates)
      ? scheduledDates
      : scheduledDates
        ? [scheduledDates]
        : [];
    const scheduledDatesForRequest = normalizedScheduledDates
      .map((entry) => {
        if (entry instanceof Date && !isNaN(entry.getTime())) {
          return formatDateTimeIso(entry);
        }

        const parsed = new Date(entry);
        return isNaN(parsed.getTime()) ? null : formatDateTimeIso(parsed);
      })
      .filter(Boolean);

    let newAlert = {
      id: alert.id,
      alertName: alert.name,
      scheduledDates: scheduledDatesForRequest,
      allGroups: alert.allGroups,
      groups: alert.groups.map((g) => g.id),
      priorityLevel: alert.isEmergency
        ? AlertPriorityLevel.Emergency
        : AlertPriorityLevel.NonEmergency,
      callLevel:
        (escalation.mode === "specify" || escalation.mode === "quick") &&
        escalation.selectedLevel !== "" &&
        escalation.selectedLevel !== undefined
          ? parseInt(escalation.selectedLevel, 10) || 0
          : 0,
      isDraft: isDraft,
    };

    if (alert.includeText) {
      newAlert.isSms = true;
      newAlert.smsMessage = alert.smsMessage;
      // Add Alert2WaySms flag based on dropdown
      newAlert.Alert2WaySms = requestResponse === "1" ? true : false;
      // Post to connected Facebook Page when the notice checkbox is ticked
      newAlert.postToFacebook =
        loginUser?.facebookPostAllow && alert.postToFacebook === true;
    }

    if (alert.includeEmail) {
      newAlert.isEmail = true;
      const body = (alert.emailMessage || "").trim();
      let isHtmlBody = /<\/?[a-z][\w-]*[^>]*>/i.test(body);
      if (isHtmlBody) {
        const m = body.match(/^<p[^>]*>\s*([\s\S]*?)\s*<\/p>$/i);
        if (m) {
          const inner = m[1].trim();
          if (!/<\/?[a-z][\w-]*[^>]*>/i.test(inner)) {
            isHtmlBody = false;
          }
        }
      }

      let emailBodyToSend = body;
      if (!isHtmlBody) {
        // Strip a single paragraph wrapper for plain text
        const m = body.match(/^<p[^>]*>\s*([\s\S]*?)\s*<\/p>$/i);
        emailBodyToSend = m ? m[1] : body;
      }

      newAlert.emailMessage = emailBodyToSend;
      if (isHtmlBody) newAlert.htmlMessage = emailBodyToSend;
      newAlert.emailSubject = alert.emailSubject || alert.name || "";
      // Backend flag: 1 = plain text, 0 = HTML
      newAlert.email_text_html = isHtmlBody ? 0 : 1;
      newAlert.attachments = alert.attachments;
      newAlert.replyTo = requestEmailResponse || "";
      newAlert.emailSenderName = alert.emailSenderName || "";
    }

    if (alert.includeVoice) {
      newAlert.isVoice = true;
      newAlert.ttsMessage = alert.t2sMessage || alert.smsMessage || "";
      // Multi-channel alerts always use the auto-generated TTS recording;
      // there is no manual "record" option shown for that flow.
      if (!isMultiType(alert) && state.voiceTab === "record") {
        // A reused Voice Recording template (or a previously-uploaded file)
        // already has a real, submittable URL here — don't overwrite it
        // with the (possibly empty) TTS conversion URL.
        newAlert.voiceFileUrl = alert.voiceFileUrl || "";
        newAlert.voiceRecording = alert.voiceRecording;
      } else {
        newAlert.voiceFileUrl = alert.t2sUrl || alert.voiceFileUrl || "";
      }
      if (alert.voiceFileIdentifier) {
        newAlert.voiceFileIdentifier = alert.voiceFileIdentifier;
      }
    }

    // Pendo Tier-1 funnel events — fire at point of user intent, before the API call
    const pendoChannels = [];
    if (alert.includeText) pendoChannels.push("sms");
    if (alert.includeEmail) pendoChannels.push("email");
    if (alert.includeVoice) pendoChannels.push("voice");

    const isScheduled = scheduledDatesForRequest.length > 0;

    if (isDraft) {
      trackPendo("alert_draft_saved", {
        channel_count: pendoChannels.length,
      });
    } else if (isScheduled) {
      const firstScheduledDate = new Date(scheduledDatesForRequest[0]);
      const scheduledDaysOut = Math.floor(
        (firstScheduledDate - new Date()) / (1000 * 60 * 60 * 24)
      );
      trackPendo("alert_scheduled", {
        channels: pendoChannels,
        scheduled_days_out: scheduledDaysOut,
      });
    } else {
      trackPendo("alert_sent", {
        channels: pendoChannels,
        channel_count: pendoChannels.length,
        is_emergency: !!alert.isEmergency,
        is_scheduled: false,
        is_all_groups: !!alert.allGroups,
        group_count: alert.groups?.length ?? 0,
        has_attachment: (alert.attachments?.length ?? 0) > 0,
        used_template: usedTemplate,
        voice_method: alert.includeVoice
          ? state.voiceTab === "record"
            ? "recording"
            : "tts"
          : null,
        two_way_sms: requestResponse === "1",
      });
    }

    actions.alerts.saveAlert(newAlert);
  }

  function isMultiType(alert) {
    const types = [alert.includeText, alert.includeEmail, alert.includeVoice];
    return types.filter(Boolean).length > 1;
  }

  function ListItem({ children, onRemove }) {
    return (
      <span>
        {children}
        <i className="fas fa-times" onClick={onRemove}></i>
      </span>
    );
  }

  function getSmsCreditCount(charCount) {
    if (charCount <= 160) return 1;
    if (charCount <= 306) return 2;
    if (charCount <= 460) return 3;
    return 4;
  }

  function SmsCreditsIndicator({ charCount }) {
    const count = charCount || 0;
    const creditCount = getSmsCreditCount(count);
    const progressPercent = Math.min((count / SMS_CHAR_LIMIT) * 100, 100);

    return (
      <div className="sms-credits-widget">
        <div className="sms-char-count-row">
          <span className="sms-char-count">
            {count}/{SMS_CHAR_LIMIT} characters
          </span>
          <div className="sms-credit-badge">
            <span>
              {creditCount} {creditCount === 1 ? "message" : "messages"}
            </span>
            <div className="sms-credit-tooltip-wrapper" tabIndex={0}>
              <i
                className="fa fa-info-circle sms-credit-info-icon"
                aria-hidden="true"
              ></i>
              <div className="sms-credit-tooltip">
                <div className="sms-credit-tooltip-title">Messages</div>
                <div className="sms-credit-tooltip-subtitle">
                  Message usage is based on message length:
                </div>
                {SmsCreditTiers.map((tier) => (
                  <div className="sms-credit-tooltip-row" key={tier.range}>
                    <span>{tier.range.replace("chars", "characters")}</span>
                    <span>{tier.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="sms-credits-indicator">
          <div className="sms-credits-progress-track">
            <div
              className="sms-credits-progress-fill"
              style={{ width: `${progressPercent}%` }}
            ></div>
            <span className="sms-credits-progress-divider sms-credits-progress-divider-1"></span>
            <span className="sms-credits-progress-divider sms-credits-progress-divider-2"></span>
            <span className="sms-credits-progress-divider sms-credits-progress-divider-3"></span>
          </div>
        </div>
      </div>
    );
  }

  function TtsCharCounter({ charCount }) {
    const count = charCount || 0;
    const nearLimit = count >= TTS_CHAR_WARNING_LIMIT;
    const estimatedSeconds = estimateTtsDurationSecondsCalibrated(
      count,
      state?.ttsCharsPerSecond,
      state?.ttsLanguage,
    );

    return (
      <div className="tts-char-counter">
        <div className="tts-char-count-row">
          <span className={"tts-char-count" + (nearLimit ? " warning" : "")}>
            {count}/{TTS_CHAR_LIMIT} characters
          </span>
          <span className="tts-estimated-duration">
            Est. length: {formatDurationShort(estimatedSeconds)}
          </span>
        </div>
        {nearLimit && (
          <div className="tts-char-warning">
            Approaching the {TTS_CHAR_LIMIT}-character limit. Longer messages
            take longer to convert and are more likely to exceed the 2 minute
            recording limit.
          </div>
        )}
      </div>
    );
  }

  function TtsDurationBanner() {
    const durationSeconds = state?.ttsDurationSeconds || 0;
    const overageSeconds = Math.max(
      0,
      Math.round(durationSeconds - MAX_TTS_DURATION_SECONDS),
    );

    return (
      <div className="tts-duration-banner">
        <div className="tts-duration-banner-message">
          This recording is {formatDurationShort(durationSeconds)} —{" "}
          {overageSeconds} second{overageSeconds === 1 ? "" : "s"} over the 2
          minute limit.
        </div>
        <div className="tts-duration-banner-actions">
          <GuiButton
            dataQa="tts-shorten-to-fit"
            className="tts-shorten-to-fit"
            disabled={state?.convertingTTS}
            onClick={handleShortenToFit}
          >
            Shorten to fit
          </GuiButton>
          <GuiButton
            dataQa="tts-trim-105"
            className="btn-outline"
            disabled={state?.convertingTTS}
            onClick={handleTrimTo105}
          >
            Trim to 1:45
          </GuiButton>
          <GuiButton
            dataQa="tts-edit-message"
            className="btn-outline"
            onClick={dismissTtsDurationBanner}
          >
            Edit message
          </GuiButton>
        </div>
      </div>
    );
  }

  function validate([alert, state]) {
    let errors = {};

    // Alert Type tab
    if (!alert.includeText && !alert.includeEmail && !alert.includeVoice) {
      errors.alertType = "Please choose an message type.";
      errors.tab0 = true;
    }

    if (
      !alert?.allGroups &&
      alert.groups.length === 0 &&
      (!alert?.users || alert?.users.length === 0)
    ) {
      errors.groups = "Please pick a group or user to receive your message.";
      errors.tab1 = true;
    }

    // Message tab
    if (!alert?.includeText && !alert?.includeEmail && !alert?.includeVoice)
      errors.tab2 = true;

    if (!alert.name) {
      errors.name = "A message name is required.";
      errors.tab2 = true;
    }

    if (isMultiType(alert)) {
      if (!alert?.smsMessage) {
        errors.smsMessage = "A text message is required.";
        errors.tab2 = true;
      }
    } else {
      if (alert?.includeText && !alert?.smsMessage) {
        errors.smsMessage = "A text message is required.";
        errors.tab2 = true;
      }
      if (alert?.includeEmail && !alert?.emailMessage) {
        errors.emailMessage = "An email body is required.";
        errors.tab2 = true;
      }
      if (alert?.includeVoice) {
        if (
          state.voiceTab === "record" &&
          !alert?.voiceRecording &&
          !alert?.voiceFileIdentifier &&
          !alert?.voiceFileUrl
        ) {
          errors.voiceRecording = "A voice recording is required.";
          errors.tab2 = true;
        }
        if (state.voiceTab !== "record" && !alert?.t2sUrl) {
          errors.voiceRecording = "A recording is required.";
          errors.tab2 = true;
        }
      }
      if (
        alert?.includeVoice &&
        state.voiceTab !== "record" &&
        state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS
      ) {
        errors.voiceRecording =
          "Voice message exceeds the 2 minute limit. Please shorten your message and convert again.";
        errors.tab2 = true;
      }
    }

    if (
      isMultiType(alert) &&
      alert?.includeVoice &&
      state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS
    ) {
      errors.voiceRecording =
        "Voice message exceeds the 2 minute limit. Please shorten your message.";
      errors.tab2 = true;
    }

    return errors;
  }

  async function handleMultiTypeNext() {
    if (!messageTabValid) return;

    // Only run TTS conversion if needed
    if (
      isMultiType(alert) &&
      alert?.smsMessage &&
      (alert?.t2sMessage !== alert?.smsMessage || !alert?.t2sUrl)
    ) {
      updateState({ convertingTTS: true, ttsDurationSeconds: null });
      const result = await actions.alerts.textToSpeech(alert.smsMessage);
      const durationSeconds = result?.success
        ? await getAudioDurationSeconds(result?.fileUrl)
        : null;
      updateState({
        convertingTTS: false,
        lastTTSMessage: alert?.smsMessage,
        ttsDurationSeconds: durationSeconds,
      });
      if (result.success) {
        dispatch("UPDATE_NEW_ALERT", {
          ...alert,
          t2sMessage: alert?.smsMessage,
          t2sUrl: result?.fileUrl,
          voiceFileIdentifier: result?.fileIdentifier,
        });
      }
      if (durationSeconds > MAX_TTS_DURATION_SECONDS) return;
    }

    changeStep(3);
  }

  return (
    <>
      <ValidationContext.Provider value={validation}>
        <div className="send-alert">
          <h2>
            Send Message
            {isMobile && (
              <i
                className="fal fa-times"
                onClick={() =>
                  howTabValid ? showPopup("confirmClose") : navigate("/")
                }
              ></i>
            )}
          </h2>

          <div className="work-area">
            {/* Alert Type */}
            <ExpandPanel
              className="expand-tile"
              title="How would you like to send it?"
              expanded={step === 0}
              complete={howTabValid}
              onHeaderClick={() => changeStep(0)}
              hasError={validation.getDisplayError("tab0")}
            >
              <div
                className={
                  "alert-type" +
                  (alert?.isEmergency || isEmergencyQuickLaunchApplied
                    ? " emergency"
                    : "")
                }
              >
                <span
                  className={alert?.includeText ? "selected" : ""}
                  onClick={(e) => toggleType("includeText")}
                >
                  <i className="fal fa-comment-alt-lines"></i>
                  <span id="text" className="alert-type-text">
                    Text
                  </span>
                </span>
                <span
                  className={alert?.includeEmail ? "selected" : ""}
                  onClick={(e) => toggleType("includeEmail")}
                >
                  <i className="fal fa-envelope"></i>
                  <span id="email" className="alert-type-text">
                    Email
                  </span>
                </span>
                <span
                  className={alert?.includeVoice ? "selected" : ""}
                  onClick={(e) => toggleType("includeVoice")}
                >
                  <i className="fal fa-microphone"></i>
                  <span id="voice" className="alert-type-text">
                    Voice
                  </span>
                </span>
              </div>

              <Error name="alertType" />
              <div className="next-button-container">
                <GuiButton
                  dataQa="step1-next"
                  disabled={!howTabValid}
                  onClick={(e) => onHowPanelHeaderClick()}
                >
                  Next
                </GuiButton>
              </div>
            </ExpandPanel>

            {/* Recipients */}
            <ExpandPanel
              className="expand-tile"
              title="Who do you want to send it to?"
              expanded={step === 1}
              complete={whoTabValid}
              onHeaderClick={() => onHowPanelHeaderClick()}
              hasError={validation.getDisplayError("tab1")}
            >
              {(alert.isEmergency || isEmergencyQuickLaunchApplied) && (
                <GuiCheckbox
                  label="Select All Groups"
                  className="all-groups"
                  name="allGroups"
                  value={alert.allGroups}
                  onChange={update}
                />
              )}
              {isTempGroupExists && (
                <div className="recipient-list individuals-list">
                  <label>Individuals:</label>
                  {loadingMembers ? (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "4px 0",
                      }}
                    >
                      <i
                        className="fas fa-spinner fa-spin"
                        aria-hidden="true"
                      />
                      <span>Loading recipients&hellip;</span>
                    </span>
                  ) : (
                    groupMembers?.map((g) => (
                      <ListItem
                        onRemove={() => {
                          setUserToDelete(g);
                          setShowDeleteMemberModal(true);
                        }}
                        key={g.userId}
                      >
                        {g.firstName} {g.lastName}
                      </ListItem>
                    ))
                  )}
                </div>
              )}
              {!alert.allGroups && (
                <div className="recipient-list">
                  <label>Group List:</label>
                  {alert?.groups.map(
                    (g) =>
                      !g.is_temp && (
                        <ListItem onRemove={(e) => removeGroup(g)} key={g.id}>
                          {g.name}
                        </ListItem>
                      ),
                  )}
                  <button
                    id="group-btn"
                    className="btn btn-outline"
                    onClick={() => showPopup("pickGroups")}
                  >
                    <i className="fas fa-plus"></i>Group
                  </button>
                </div>
              )}
              <Error name="groups" />

              {/* Alert Escalation Level */}
              <div className="alert-escalation-group">
                <label className="group-label">Alert Escalation Level:</label>
                <div className="radio-row row1">
                  <label className="radio-item">
                    <input
                      type="radio"
                      name="escalationMode"
                      value="specify"
                      className="alert-escalation-input"
                      checked={escalation.mode === "specify"}
                      onChange={() =>
                        actions.escalation.setSelection(
                          "specify",
                          escalation.selectedLevel,
                        )
                      }
                    />
                    <span>Specify level</span>
                  </label>
                </div>
                <div className="radio-row row2">
                  {escalation.mode !== "emergency" && (
                    <div className="level-select">
                      <GuiSelect
                        name="escalationLevel"
                        value={escalation.selectedLevel}
                        options={escalationLevelOptions}
                        disabled={escalation.mode !== "specify"} // disabled in quick mode
                        onChange={(name, item) => {
                          if (escalation.mode === "specify") {
                            actions.escalation.setSelection(
                              "specify",
                              item.value,
                            );
                          }
                        }}
                        placeholder="Select"
                      />
                    </div>
                  )}
                </div>
                <div className="radio-row row3">
                  <label className="radio-item">
                    <input
                      type="radio"
                      name="escalationMode"
                      className="alert-escalation-input"
                      value="quick"
                      checked={escalation.mode === "quick"}
                      onChange={() => {
                        actions.escalation.setSelection("quick", "");
                        applyEmergencyQuickLaunch();
                      }}
                    />
                    <span>Emergency Alert</span>
                  </label>
                </div>
              </div>

              <div className="next-button-container">
                <GuiButton
                  dataQa="step2-done"
                  disabled={!whoTabValid}
                  onClick={(e) => changeStep(2)}
                >
                  Next
                </GuiButton>
              </div>
            </ExpandPanel>

            {/* Create Message */}
            <ExpandPanel
              className="expand-tile"
              title="Create your message"
              expanded={step === 2}
              complete={messageTabValid}
              onHeaderClick={() => changeStep(2)}
              hasError={validation.getDisplayError("tab2")}
            >
              {!isMultiType(alert) && (
                <>
                  {alert?.includeEmail && (
                    <GuiButton
                      className="btn-outline email-template"
                      onClick={() => onShowTemplatePicker(AlertType.Email)}
                    >
                      <div className="choose-template"></div>Choose Template
                    </GuiButton>
                  )}

                  {alert?.includeVoice && (
                    <GuiButton
                      className="btn-outline email-template"
                      onClick={() => onShowTemplatePicker("voice")}
                    >
                      <div className="choose-template"></div>Choose Template
                    </GuiButton>
                  )}
                </>
              )}

              {alert?.includeText && (
                <>
                  <GuiButton
                    className="btn-outline email-template"
                    onClick={() => onShowTemplatePicker(AlertType.Text)}
                  >
                    <div className="choose-template"></div>Choose Template
                  </GuiButton>
                  {/* 2-way text dropdown */}
                  <div className="txt-recipients-response-label">
                    <label htmlFor="ddl2WayText">
                      Text Recipient Response:
                    </label>
                  </div>
                  <div className="txt-recipients-response">
                    <GuiSelect
                      name="ddl2WayText"
                      value={requestResponse}
                      options={[
                        {
                          value: "1",
                          label: "Request recipient message response",
                        },
                        {
                          value: "0",
                          label: "No response requested from recipient",
                        },
                      ]}
                      onChange={(name, item) => setRequestResponse(item.value)}
                      placeholder="Select response option"
                    />
                  </div>
                </>
              )}
              {alert?.includeEmail && (
                <div className="sender-reply-row">
                  <div className="sender-reply-field sender-reply-field--sender">
                    <div className="sender-reply-input-border">
                      <div className="sender-reply-input-inner">
                        <input
                          className="sender-reply-input"
                          placeholder="Sender Name"
                          value={alert?.emailSenderName || ""}
                          onChange={(e) =>
                            update("emailSenderName", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="sender-reply-label-chip">
                      <span className="sender-reply-label-text">
                        Sender Name
                      </span>
                    </div>
                  </div>

                  <div className="sender-reply-field sender-reply-field--reply">
                    <div className="sender-reply-select-border">
                      <GuiSelect
                        name="replyTo"
                        value={requestEmailResponse || ""}
                        options={emailDataSender.map((e) => ({
                          value: e.email?.toString() || "",
                          label: e.email || "",
                        }))}
                        onChange={(name, item) =>
                          setRequestEmailResponse(item.value)
                        }
                        placeholder="Select"
                      />
                    </div>
                    <div
                      className="sender-reply-label-chip"
                      style={{ top: -9 }}
                    >
                      <span className="sender-reply-label-text">
                        Reply-To Email
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div
                className={
                  alert?.includeEmail
                    ? "subject-wrapper subject-wrapper--email"
                    : "subject-wrapper"
                }
              >
                <GuiTextbox
                  dataQa="subject"
                  className="subject"
                  label="Subject"
                  name="name"
                  value={alert?.name || ""}
                  onChange={(name, value) => {
                    dispatch("UPDATE_NEW_ALERT", {
                      ...alert,
                      name: value,
                      emailSubject: value,
                    });
                    updateState({ previewed: false });
                  }}
                  maxLength={100}
                />
              </div>

              {/* Shared hidden file input for all attach actions */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.tiff,.tif,.bmp,.png,.doc,.docx,.pdf,.ppt,.pptx,.xls,.xlsx,.zip,.mp3,.3gp,.mp4,.avi,.mov,.wav,.m4a"
                style={{ display: "none" }}
                onChange={onFileAttachSelected}
              />

              {/* If multi-type, show only Text message field and sync to others */}
              {isMultiType(alert) ? (
                <>
                  {alert?.includeText && (
                    <>
                      <div className="sms-message-header">
                        <span className="sms-message-label">Message</span>
                      </div>
                      {!isMobile && (
                        <SmsCreditsIndicator
                          charCount={alert?.smsMessage?.length || 0}
                        />
                      )}
                    </>
                  )}
                  <GuiTextArea
                    dataQa="text-subject"
                    name="smsMessage"
                    value={alert?.smsMessage}
                    maxLength={SMS_CHAR_LIMIT}
                    onChange={(name, value) => {
                      if (value.length > SMS_CHAR_LIMIT) {
                        value = value.slice(0, SMS_CHAR_LIMIT);
                      }
                      dispatch("UPDATE_NEW_ALERT", {
                        ...alert,
                        smsMessage: value,
                        emailMessage: value, // Sync email body
                        name: alert.name,
                        emailSubject: alert.name, // Sync subject if needed
                        t2sMessage: value,
                        t2sUrl: "",
                        voiceFileIdentifier: "",
                      });
                      updateState({
                        previewed: false,
                        ttsTrim: null,
                        ttsDurationSeconds: null,
                        ttsCharsPerSecond: null,
                        ttsBannerDismissed: false,
                      });
                    }}
                    onPaste={(e) => {
                      const paste = (
                        e.clipboardData || window.clipboardData
                      ).getData("text");
                      const newValue = (alert?.smsMessage || "") + paste;
                      if (newValue.length > SMS_CHAR_LIMIT) {
                        e.preventDefault();
                        dispatch("UPDATE_NEW_ALERT", {
                          ...alert,
                          smsMessage: newValue.slice(0, SMS_CHAR_LIMIT),
                          t2sUrl: "",
                          voiceFileIdentifier: "",
                        });
                        updateState({
                          previewed: false,
                          ttsTrim: null,
                          ttsDurationSeconds: null,
                          ttsCharsPerSecond: null,
                          ttsBannerDismissed: false,
                        });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (
                        alert?.smsMessage &&
                        alert?.smsMessage.length >= SMS_CHAR_LIMIT &&
                        ![
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                  {isMobile && (
                    <SmsCreditsIndicator
                      charCount={alert?.smsMessage?.length || 0}
                    />
                  )}
                  <div className="attach-file-row">
                    {isFileUploading ? (
                      <button className="attach-file-loading-btn" disabled>
                        <span className="attach-file-spinner"></span>
                      </button>
                    ) : (
                      <GuiButton
                        className="attach-file-btn btn-outline"
                        onClick={() => onFileAttachClicked("sms")}
                      >
                        <i className="far fa-paperclip"></i>
                        {" Attach File"}
                      </GuiButton>
                    )}
                    <div className="attach-file-hint">
                      File will attach as a short link
                    </div>
                  </div>
                  <TranslationControls
                    type="text"
                    sourceText={alert?.smsMessage || ""}
                    includeTTS={alert?.includeVoice}
                    onTranslated={(translated) => {
                      dispatch("UPDATE_NEW_ALERT", {
                        ...alert,
                        smsMessage: translated,
                        emailMessage: isMultiType(alert)
                          ? translated
                          : alert?.emailMessage,
                        t2sMessage: isMultiType(alert)
                          ? translated
                          : alert?.t2sMessage,
                        t2sUrl: isMultiType(alert) ? "" : alert?.t2sUrl,
                        voiceFileIdentifier: isMultiType(alert)
                          ? ""
                          : alert?.voiceFileIdentifier,
                      });
                      updateState({
                        previewed: false,
                        lastTTSMessage: isMultiType(alert)
                          ? null
                          : state?.lastTTSMessage,
                      });
                    }}
                  />
                  {(!alert?.name?.trim() || !alert?.smsMessage?.trim()) && (
                    <div className="error-class">
                      You must fill in both the subject and message fields to
                      proceed.
                    </div>
                  )}

                  {renderSpamWarnings("text")}

                  {state.ttsDurationSeconds > 0 && (
                    <div className="tts-measured-duration">
                      Converted length:{" "}
                      {formatDurationShort(state.ttsDurationSeconds)}
                    </div>
                  )}
                  {state.ttsTrim?.overflowText && (
                    <div className="tts-trim-preview">
                      <span className="tts-trim-kept">
                        {state.ttsTrim.keptText}
                      </span>{" "}
                      <span
                        className="tts-trim-overflow"
                        title="Not included in the voice recording — exceeds the 2 minute limit"
                      >
                        {state.ttsTrim.overflowText}
                      </span>
                    </div>
                  )}
                  {state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS &&
                    !state.ttsBannerDismissed && <TtsDurationBanner />}

                  <div className="next-button-container">
                    <GuiButton
                      dataQa="use-recording"
                      disabled={!messageTabValid || state?.convertingTTS}
                      onClick={handleMultiTypeNext}
                    >
                      {state?.convertingTTS ? <Spinner /> : "Next"}
                    </GuiButton>
                  </div>
                </>
              ) : (
                <>
                  {/* Existing logic for single type selection */}
                  {alert?.includeText && (
                    <>
                      <div className="sms-message-header">
                        <span className="sms-message-label">Message</span>
                      </div>
                      {!isMobile && (
                        <SmsCreditsIndicator
                          charCount={alert?.smsMessage?.length || 0}
                        />
                      )}
                      <GuiTextArea
                        dataQa="text-subject"
                        name="smsMessage"
                        value={alert?.smsMessage}
                        maxLength={SMS_CHAR_LIMIT}
                        onChange={(name, value) => {
                          if (value.length > SMS_CHAR_LIMIT)
                            value = value.slice(0, SMS_CHAR_LIMIT);
                          update(name, value);
                        }}
                        onPaste={(e) => {
                          const paste = (
                            e.clipboardData || window.clipboardData
                          ).getData("text");
                          const newValue = (alert?.smsMessage || "") + paste;
                          if (newValue.length > SMS_CHAR_LIMIT) {
                            e.preventDefault();
                            update(
                              "smsMessage",
                              newValue.slice(0, SMS_CHAR_LIMIT),
                            );
                          }
                        }}
                        onKeyDown={(e) => {
                          if (
                            alert?.smsMessage &&
                            alert?.smsMessage.length >= SMS_CHAR_LIMIT &&
                            ![
                              "Backspace",
                              "Delete",
                              "ArrowLeft",
                              "ArrowRight",
                              "Tab",
                            ].includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                      {isMobile && (
                        <SmsCreditsIndicator
                          charCount={alert?.smsMessage?.length || 0}
                        />
                      )}
                      <div className="attach-file-row">
                        {isFileUploading ? (
                          <button className="attach-file-loading-btn" disabled>
                            <span className="attach-file-spinner"></span>
                          </button>
                        ) : (
                          <GuiButton
                            className="attach-file-btn btn-outline"
                            onClick={() => onFileAttachClicked("sms")}
                          >
                            <i className="far fa-paperclip"></i>
                            {" Attach File"}
                          </GuiButton>
                        )}
                        <div className="attach-file-hint">
                          File will attach as a short link
                        </div>
                      </div>
                      <TranslationControls
                        type="text"
                        sourceText={alert?.smsMessage || ""}
                        includeTTS={alert?.includeVoice}
                        onTranslated={(translated) => {
                          dispatch("UPDATE_NEW_ALERT", {
                            ...alert,
                            smsMessage: translated,
                          });
                          updateState({ previewed: false });
                        }}
                      />
                      {(!alert?.name?.trim() || !alert?.smsMessage?.trim()) && (
                        <div className="error-class">
                          You must fill in both the subject and message fields
                          to proceed.
                        </div>
                      )}

                      {renderSpamWarnings("text")}
                    </>
                  )}

                  {alert?.includeEmail && (
                    <>
                      <GuiHtmlEditor
                        id="email-body"
                        label="Message"
                        name="emailMessage"
                        value={alert?.emailMessage}
                        onChange={update}
                        onAttachmentAdded={onAttachmentAdded}
                        fakeRef={emailHtmlEditor}
                      />
                      <div className="attach-file-row">
                        {isFileUploading ? (
                          <button className="attach-file-loading-btn" disabled>
                            <span className="attach-file-spinner"></span>
                          </button>
                        ) : (
                          <GuiButton
                            className="attach-file-btn btn-outline"
                            onClick={() => onFileAttachClicked("email")}
                          >
                            <i className="far fa-paperclip"></i>
                            {" Attach File"}
                          </GuiButton>
                        )}
                        <div className="attach-file-hint">
                          File will attach as a short link
                        </div>
                      </div>
                      <TranslationControls
                        type="email"
                        sourceText={(alert?.emailMessage || "")
                          .replace(/<[^>]+>/g, " ")
                          .replace(/\s+/g, " ")
                          .trim()}
                        includeTTS={alert?.includeVoice}
                        onTranslated={(translated) => {
                          dispatch("UPDATE_NEW_ALERT", {
                            ...alert,
                            emailMessage: translated,
                          });
                          updateState({ previewed: false });
                          if (emailHtmlEditor.current?.setHtml) {
                            emailHtmlEditor.current.setHtml(translated);
                          }
                        }}
                      />
                      <div className="attachments">
                        {alert?.attachments.map((a) => (
                          <div className="attachment" key={a.url}>
                            <i className="icon far fa-paperclip"></i>
                            {a.name}
                            <i
                              className="close fal fa-times"
                              onClick={(e) => removeAttachment(a)}
                            ></i>
                          </div>
                        ))}
                      </div>
                      {renderSpamWarnings("email")}
                    </>
                  )}

                  {alert?.includeVoice && (
                    <>
                      <div className="voice-text-toggle">
                        <div id="voice-recording" className="voice-recording">
                          <TabItem
                            tab="record"
                            currentTab={state?.voiceTab}
                            onClick={(e) => updateState({ voiceTab: "record" })}
                          >
                            Voice Recording
                          </TabItem>
                        </div>
                        <div id="text-to-voice" className="text-to-voice">
                          <TabItem
                            tab="texttospeech"
                            currentTab={state?.voiceTab}
                            onClick={(e) =>
                              updateState({ voiceTab: "texttospeech" })
                            }
                          >
                            Text to Voice
                          </TabItem>
                        </div>
                        {/* Tab for future Callback option */}
                        {/*<div className="text-to-voice"><TablItem tab="callback" currentTab={state.voiceTab} onClick={e => setVoiceTab("callback")}>Callback</TablItem></div>*/}
                      </div>

                      {state?.voiceTab === "record" && (
                        <VoiceRecorder
                          name="voiceRecording"
                          value={
                            alert?.voiceRecording ||
                            alert?.voicePreviewUrl ||
                            alert?.voiceFileUrl
                          }
                          onChange={updateVoiceRecording}
                          error={validation.getDisplayError("voiceRecording")}
                          autoPlay={true}
                        />
                      )}

                      {state?.voiceTab === "texttospeech" && (
                        <div className="text-to-speech">
                          <GuiTextArea
                            dataQa="body"
                            label={
                              "Message" +
                              (state?.lastTTSMessage &&
                              state?.lastTTSMessage !== alert?.t2sMessage
                                ? " *"
                                : "")
                            }
                            name="t2sMessage"
                            value={alert?.t2sMessage}
                            maxLength={TTS_CHAR_LIMIT}
                            onChange={handleTtsMessageChange}
                            onAttachmentAdded={onAttachmentAdded}
                          />
                          <TtsCharCounter
                            charCount={alert?.t2sMessage?.length || 0}
                          />
                          <TranslationControls
                            type="voice"
                            sourceText={alert?.t2sMessage || ""}
                            includeTTS={true}
                            onTranslated={(translated, language) => {
                              dispatch("UPDATE_NEW_ALERT", {
                                ...alert,
                                t2sMessage: (translated || "").slice(
                                  0,
                                  TTS_CHAR_LIMIT,
                                ),
                                t2sUrl: "",
                                voiceFileIdentifier: "",
                              });
                              updateState({
                                previewed: false,
                                lastTTSMessage: null,
                                ttsLanguage: language,
                                ttsDurationSeconds: null,
                                ttsCharsPerSecond: null,
                                ttsTrim: null,
                                ttsBannerDismissed: false,
                              });
                            }}
                          />

                          {renderSpamWarnings("voice")}

                          {!alert?.t2sUrl && (
                            <div className="next-button-container">
                              <GuiButton
                                dataQa="convert"
                                disabled={!alert?.t2sMessage}
                                onClick={convertTTS}
                              >
                                {state?.convertingTTS ? (
                                  <Spinner />
                                ) : (
                                  "Convert to Voice"
                                )}
                              </GuiButton>
                            </div>
                          )}
                          {alert?.t2sUrl && (
                            <>
                              <div className="player">
                                <AudioPlayer
                                  src={alert?.t2sUrl}
                                  autoPlay={true}
                                />
                              </div>
                              {state.ttsDurationSeconds > 0 && (
                                <div className="tts-measured-duration">
                                  Converted length:{" "}
                                  {formatDurationShort(
                                    state.ttsDurationSeconds,
                                  )}
                                </div>
                              )}
                              {state.ttsTrim?.overflowText && (
                                <div className="tts-trim-preview">
                                  <span className="tts-trim-kept">
                                    {state.ttsTrim.keptText}
                                  </span>{" "}
                                  <span
                                    className="tts-trim-overflow"
                                    title="Not included in the voice recording — exceeds the 2 minute limit"
                                  >
                                    {state.ttsTrim.overflowText}
                                  </span>
                                </div>
                              )}
                              {state.ttsDurationSeconds >
                                MAX_TTS_DURATION_SECONDS &&
                                !state.ttsBannerDismissed && (
                                  <TtsDurationBanner />
                                )}
                              <div className="next-button-container">
                                <GuiButton
                                  dataQa="update"
                                  className="btn-outline update-tts"
                                  disabled={
                                    !alert?.t2sMessage ||
                                    state?.lastTTSMessage === alert?.t2sMessage
                                  }
                                  onClick={convertTTS}
                                >
                                  {state.convertingTTS ? <Spinner /> : "Update"}
                                </GuiButton>
                                <GuiButton
                                  dataQa="use-recording"
                                  disabled={!messageTabValid}
                                  onClick={(e) => changeStep(3)}
                                >
                                  Use Recording
                                </GuiButton>
                              </div>
                              {!messageTabValid && (
                                <div className="tts-blocking-reason">
                                  {validation.getError("name") ||
                                    validation.getError("voiceRecording") ||
                                    "Please complete the required fields to continue."}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {(!alert?.includeVoice ||
                    state?.voiceTab !== "texttospeech") && (
                    <div className="next-button-container">
                      <GuiButton
                        dataQa="use-recording"
                        disabled={!messageTabValid}
                        onClick={(e) => changeStep(3)}
                      >
                        {alert?.includeVoice ? "Use Recording" : "Next"}
                      </GuiButton>
                    </div>
                  )}
                  {!messageTabValid &&
                    (!alert?.includeVoice ||
                      state?.voiceTab !== "texttospeech") && (
                      <div className="tts-blocking-reason">
                        {validation.getError("name") ||
                          validation.getError("smsMessage") ||
                          validation.getError("emailMessage") ||
                          validation.getError("voiceRecording") ||
                          "Please complete the required fields to continue."}
                      </div>
                    )}
                </>
              )}
            </ExpandPanel>

            <ExpandPanel
              title="Preview and Send"
              className="preview expand-tile"
              expanded={step === 3}
              complete={state?.previewed}
              onHeaderClick={() => {
                if (!state.convertingTTS) changeStep(3);
              }}
              disabled={state.convertingTTS}
            >
              {/* Selected Types with Green Check */}
              <div className="check-icon-class">
                {alert?.includeText && (
                  <span className="green-check">
                    <i className="fas fa-check green-check-icon"></i> Text
                  </span>
                )}
                {alert?.includeEmail && (
                  <span className="green-check">
                    <i className="fas fa-check green-check-icon"></i> Email
                  </span>
                )}
                {alert?.includeVoice && (
                  <span className="green-check">
                    <i className="fas fa-check green-check-icon"></i> Voice
                  </span>
                )}
              </div>
              <label>Message Title</label>
              <div>{alert?.name}</div>
              {alert?.isScheduled && (
                <>
                  <label>Scheduled For</label>
                  <div>{formatGridDate(alert?.scheduledDate)}</div>
                </>
              )}
              <label>Recipients</label>
              <div>
                {alert?.allGroups
                  ? "All Groups"
                  : alert?.groups.map((r) => r.name).join(", ")}
              </div>
              {alert?.includeText && (
                <>
                  <label>Preview Text Message</label>
                  <div className="break-word">{alert?.smsMessage}</div>
                </>
              )}
              {alert?.includeEmail && (
                <>
                  <label>Preview Email</label>
                  <div className="box-card">
                    <div className="box-subject">
                      <label>Subject:</label> {alert?.name}
                    </div>
                    <div
                      className="box-body break-word"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(alert?.emailMessage),
                      }}
                    ></div>
                  </div>
                </>
              )}
              {alert?.includeVoice && (
                <>
                  <label>Voice Message</label>
                  {state?.convertingTTS ? (
                    <Spinner />
                  ) : alert?.voiceRecording ? (
                    <AudioPlayer src={alert?.voiceRecording} autoPlay={false} />
                  ) : alert?.t2sUrl ? (
                    <AudioPlayer src={alert?.t2sUrl} autoPlay={false} />
                  ) : alert?.voicePreviewUrl || alert?.voiceFileUrl ? (
                    <AudioPlayer
                      src={alert?.voicePreviewUrl || alert?.voiceFileUrl}
                      autoPlay={false}
                    />
                  ) : (
                    <div className="voice-preview-error">
                      Voice preview will appear after conversion or recording.
                    </div>
                  )}
                  {alert?.t2sUrl && state.ttsDurationSeconds > 0 && (
                    <div className="tts-measured-duration">
                      Converted length:{" "}
                      {formatDurationShort(state.ttsDurationSeconds)}
                    </div>
                  )}
                  {alert?.t2sUrl &&
                    state.ttsDurationSeconds > MAX_TTS_DURATION_SECONDS &&
                    !state.ttsBannerDismissed && <TtsDurationBanner />}
                </>
              )}
              {alert?.includeText && loginUser?.facebookPostAllow && (
                <>
                  <div className="post-to-facebook-row">
                    <svg
                      className="post-to-facebook-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      aria-hidden="true"
                    >
                      <path
                        fill="#1877f2"
                        d="M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256c0 120 82.7 220.8 194.2 248.5V334.2h-56.6V256h56.6v-58.2c0-56.1 33.1-87.2 83.8-87.2 24.6 0 50.5 4.4 50.5 4.4v55h-28.4c-28 0-36.8 17.4-36.8 35.3V256h61.8l-9.9 78.2h-51.9v170.3C429.3 476.8 512 376 512 256z"
                      />
                    </svg>
                    <GuiCheckbox
                      label="Post to Facebook"
                      className="post-to-facebook"
                      name="postToFacebook"
                      value={alert?.postToFacebook}
                      onChange={(name, value) =>
                        dispatch("UPDATE_NEW_ALERT", {
                          ...alert,
                          [name]: value,
                        })
                      }
                    />
                  </div>
                  {alert?.postToFacebook && (
                    <div className="facebook-post-notice">
                      <svg
                        className="facebook-post-notice-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        aria-hidden="true"
                      >
                        <path
                          fill="#1877f2"
                          d="M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256c0 120 82.7 220.8 194.2 248.5V334.2h-56.6V256h56.6v-58.2c0-56.1 33.1-87.2 83.8-87.2 24.6 0 50.5 4.4 50.5 4.4v55h-28.4c-28 0-36.8 17.4-36.8 35.3V256h61.8l-9.9 78.2h-51.9v170.3C429.3 476.8 512 376 512 256z"
                        />
                      </svg>
                      <span>
                        This message will also be posted to your connected
                        Facebook Page. Scheduled alerts will create a scheduled
                        Facebook post for the same time.
                      </span>
                    </div>
                  )}
                </>
              )}
            </ExpandPanel>
          </div>

          {/* Buttons */}
          {isMobile && !alert?.isEmergency ? (
            <div className="send-alert-bottom-button-bar-mobile">
              {!hasSpamContent && (
                <>
                  <GuiButton
                    dataQa="schedule"
                    className="btn-outline schedule"
                    disabled={
                      !state?.previewed || !messageTabValid || loadingMembers
                    }
                    onClick={(event) => scheduleClick(event)}
                  >
                    Schedule
                  </GuiButton>
                  <GuiButton
                    dataQa="send"
                    className="send"
                    disabled={
                      !state?.previewed || !messageTabValid || loadingMembers
                    }
                    onClick={save}
                  >
                    Send
                  </GuiButton>
                </>
              )}
            </div>
          ) : (
            <div className="send-alert-bottom-button-bar">
              <div className="bottom-button-bar-discard-draft">
                {!isMobile && (
                  <>
                    <GuiButton
                      dataQa="discard"
                      className="btn-outline"
                      onClick={(e) => showPopup("confirmDiscard")}
                    >
                      Discard
                    </GuiButton>
                  </>
                )}
                <GuiButton
                  dataQa="save-draft"
                  className="btn-outline save-as"
                  disabled={!howTabValid}
                  onClick={(e) => howTabValid && save(true)}
                >
                  Save Draft
                </GuiButton>
              </div>
              <div className="bottom-button-bar-schedule-send">
                {!alert.isEmergency && !hasSpamContent && (
                  <GuiButton
                    dataQa="schedule"
                    className="btn-outline"
                    disabled={
                      !state?.previewed || !messageTabValid || loadingMembers
                    }
                    onClick={(event) => scheduleClick(event)}
                  >
                    Schedule
                  </GuiButton>
                )}
                {!hasSpamContent && (
                  <GuiButton
                    dataQa="send"
                    className="send"
                    disabled={
                      !state?.previewed || !messageTabValid || loadingMembers
                    }
                    onClick={save}
                  >
                    Send
                  </GuiButton>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modals and Popups */}
        <LoadingSpinner show={saving} className="loading-page" />

        <GroupPicker
          show={pickGroups}
          selected={alert?.groups}
          onDone={groupsChanged}
          onBack={hidePopup}
        />
        {pickTemplate && (
          <TemplatePickerModal
            alertType={
              state?.templatePickerType === "voice"
                ? null
                : state?.templatePickerType
            }
            voiceCombined={state?.templatePickerType === "voice"}
            onInsert={useTemplate}
            onCancel={hidePopup}
          />
        )}
        {confirmSave && (
          <ScheduleBaseAlertModal
            value={alert?.scheduledDates || alert?.scheduledDate}
            onSave={saveSchedule}
            onCancel={closeScheduleModal}
          />
        )}
        {confirmClose && (
          <ConfirmCloseModal
            onDelete={discardAlert}
            onSaveDraft={(e) => save(true)}
            onCancel={hidePopup}
          />
        )}
        {confirmDiscard && (
          <ConfirmDeleteModal
            status="discard"
            onDelete={discardAlert}
            onCancel={hidePopup}
          />
        )}
      </ValidationContext.Provider>

      {/* Delete */}
      {showDeleteMemberModal && userToDelete && (
        <Modal
          className="plain-modal no-close"
          showModal={showDeleteMemberModal}
          onClose={() => {
            setShowDeleteMemberModal(false);
            setUserToDelete(null);
          }}
        >
          <p>
            Are you sure you want to remove {userToDelete?.firstName}{" "}
            {userToDelete?.lastName} from the individuals?
          </p>
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={() => {
                setShowDeleteMemberModal(false);
                setUserToDelete(null);
              }}
            >
              Cancel
            </GuiButton>
            <GuiButton
              className="warning"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveUser(userToDelete?.userId);
                setShowDeleteMemberModal(false);
                setUserToDelete(null);
              }}
            >
              Delete {userToDelete?.firstName} {userToDelete?.lastName}
            </GuiButton>
          </div>
        </Modal>
      )}
    </>
  );
}
